import React from 'react';
import { Link } from 'react-router-dom';
import { Box, Container, Typography, Grid, IconButton, Button } from '@mui/material';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import FacebookIcon from '@mui/icons-material/Facebook';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { colors } from '../theme';

const Footer: React.FC = () => {
  const currentYear = new Date().getFullYear();

  const quickLinks = [
    { label: 'Home', path: '/' },
    { label: 'About Us', path: '/about' },
    { label: 'Services', path: '/services' },
    { label: 'Contact Us', path: '/contact' },
  ];

  const services = [
    'Control Panel Fabrication',
    'Combination Starter Panels',
    'PLC Panels',
    'Soft Start Panels',
    'Onsite Services',
    'Process Equipment Sales',
  ];

  return (
    <Box
      component="footer"
      sx={{
        mt: 'auto',
      }}
    >
      {/* Main Footer Content */}
      <Box sx={{ bgcolor: colors.secondary, py: { xs: 8, md: 10 } }}>
        <Container maxWidth="lg">
          <Grid container spacing={6}>
            {/* Logo & About */}
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
                <Box
                  component="img"
                  src="/Logo-RPM.png"
                  alt="RPM Control"
                  sx={{
                    height: 45,
                    width: 'auto',
                    filter: 'brightness(0) invert(1)',
                    pr: 2,
                    borderRight: '1px solid rgba(255, 255, 255, 0.2)',
                  }}
                />
                <Box
                  component="img"
                  src="/Logo-Process.png"
                  alt="Process Control"
                  sx={{
                    height: 45,
                    width: 'auto',
                    filter: 'brightness(0) invert(1)',
                  }}
                />
              </Box>
              <Typography
                variant="body2"
                sx={{
                  color: 'rgba(255, 255, 255, 0.7)',
                  lineHeight: 1.9,
                  mb: 4,
                  maxWidth: 300,
                }}
              >
                Delivering precision electrical engineering and automation solutions 
                for industrial facilities. Custom control panels, VFD repackaging, 
                and process equipment sales.
              </Typography>
              <Box sx={{ display: 'flex', gap: 1.5 }}>
                <IconButton
                  href="https://linkedin.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    color: 'rgba(255, 255, 255, 0.6)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: 2,
                    width: 44,
                    height: 44,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      color: colors.primary,
                      borderColor: colors.primary,
                      backgroundColor: 'rgba(245, 166, 35, 0.1)',
                    },
                  }}
                >
                  <LinkedInIcon />
                </IconButton>
                <IconButton
                  href="https://facebook.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    color: 'rgba(255, 255, 255, 0.6)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: 2,
                    width: 44,
                    height: 44,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      color: colors.primary,
                      borderColor: colors.primary,
                      backgroundColor: 'rgba(245, 166, 35, 0.1)',
                    },
                  }}
                >
                  <FacebookIcon />
                </IconButton>
              </Box>
            </Grid>

            {/* Quick Links */}
            <Grid item xs={6} md={2}>
              <Typography
                sx={{
                  fontSize: '0.875rem',
                  fontWeight: 700,
                  color: colors.primary,
                  mb: 3,
                  textTransform: 'uppercase',
                  letterSpacing: 2,
                }}
              >
                Navigation
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {quickLinks.map((link) => (
                  <Link
                    key={link.label}
                    to={link.path}
                    style={{ textDecoration: 'none' }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'rgba(255, 255, 255, 0.7)',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          color: colors.primary,
                          transform: 'translateX(4px)',
                        },
                      }}
                    >
                      {link.label}
                    </Typography>
                  </Link>
                ))}
              </Box>
            </Grid>

            {/* Services */}
            <Grid item xs={6} md={3}>
              <Typography
                sx={{
                  fontSize: '0.875rem',
                  fontWeight: 700,
                  color: colors.primary,
                  mb: 3,
                  textTransform: 'uppercase',
                  letterSpacing: 2,
                }}
              >
                Services
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {services.map((service) => (
                  <Typography
                    key={service}
                    variant="body2"
                    sx={{
                      color: 'rgba(255, 255, 255, 0.7)',
                    }}
                  >
                    {service}
                  </Typography>
                ))}
              </Box>
            </Grid>

            {/* Contact Info */}
            <Grid item xs={12} md={3}>
              <Typography
                sx={{
                  fontSize: '0.875rem',
                  fontWeight: 700,
                  color: colors.primary,
                  mb: 3,
                  textTransform: 'uppercase',
                  letterSpacing: 2,
                }}
              >
                Contact
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: 2,
                      backgroundColor: 'rgba(245, 166, 35, 0.15)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <PhoneIcon sx={{ color: colors.primary, fontSize: 20 }} />
                  </Box>
                  <Typography
                    component="a"
                    href="tel:3154797262"
                    variant="body2"
                    sx={{
                      color: 'rgba(255, 255, 255, 0.7)',
                      textDecoration: 'none',
                      transition: 'color 0.2s ease',
                      '&:hover': { color: colors.primary },
                    }}
                  >
                    315-479-7262
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: 2,
                      backgroundColor: 'rgba(245, 166, 35, 0.15)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <EmailIcon sx={{ color: colors.primary, fontSize: 20 }} />
                  </Box>
                  <Typography
                    component="a"
                    href="mailto:jsmith@processcontrolllc.com"
                    variant="body2"
                    sx={{
                      color: 'rgba(255, 255, 255, 0.7)',
                      textDecoration: 'none',
                      transition: 'color 0.2s ease',
                      '&:hover': { color: colors.primary },
                    }}
                  >
                    jsmith@processcontrolllc.com
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: 2,
                      backgroundColor: 'rgba(245, 166, 35, 0.15)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <LocationOnIcon sx={{ color: colors.primary, fontSize: 20 }} />
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'rgba(255, 255, 255, 0.7)',
                      lineHeight: 1.7,
                    }}
                  >
                    3721 New Court Avenue
                    <br />
                    Syracuse, NY 13206 USA
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* CTA Bar */}
      <Box
        sx={{
          background: `linear-gradient(90deg, ${colors.primary} 0%, ${colors.primaryDark} 100%)`,
          py: 4,
        }}
      >
        <Container maxWidth="lg">
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 3,
            }}
          >
            <Box>
              <Typography
                sx={{
                  fontSize: { xs: '1.25rem', md: '1.5rem' },
                  fontWeight: 700,
                  color: '#FFFFFF',
                  textAlign: { xs: 'center', md: 'left' },
                }}
              >
                Ready to start your project?
              </Typography>
              <Typography
                sx={{
                  fontSize: '1rem',
                  color: 'rgba(255, 255, 255, 0.85)',
                  textAlign: { xs: 'center', md: 'left' },
                }}
              >
                Get a quote within 24 business hours on most quotes!
              </Typography>
            </Box>
            <Button
              component={Link}
              to="/contact"
              variant="contained"
              size="large"
              endIcon={<ArrowForwardIcon />}
              sx={{
                backgroundColor: '#FFFFFF',
                color: colors.primary,
                px: 4,
                py: 1.5,
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  boxShadow: '0 8px 30px rgba(255, 255, 255, 0.3)',
                },
              }}
            >
              Request a Quote
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Copyright Bar */}
      <Box
        sx={{
          background: colors.secondaryDark,
          py: 2.5,
        }}
      >
        <Container maxWidth="lg">
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: 'rgba(255, 255, 255, 0.5)',
                fontSize: '0.875rem',
                textAlign: 'center',
              }}
            >
              © {currentYear} By RPM Control & Process Control. All rights reserved.
            </Typography>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default Footer;
